import type { ChangesColumnFilterKey } from '../components/ChangesTable';
import type { FiltroPorPeriodoValue } from '../components/FiltroPorPeriodo';
import { periodToApiRange } from './defaultDateRange';

export type DashboardColumnFilters = Record<ChangesColumnFilterKey, string[]>;

/** Contexto compartilhado de pesquisa no dashboard (tabela, gráficos, breakdowns). */
export interface DashboardSearchContext {
  period: FiltroPorPeriodoValue;
  publicationDateMin: string | null;
  agencyNames: string[];
  agencyKeys: string[];
  assunto: string | null;
  keyword: string | null;
  columnFilters: DashboardColumnFilters;
  monitoredNormAssunto: boolean;
  monitoredAgency: boolean;
  greeningQaOnly: boolean;
}

export interface DashboardStatsContextFilters {
  assunto?: string | null;
  states?: string;
  legislation_types?: string;
  change_types?: string;
}

function resolveAgencyNames(ctx: DashboardSearchContext): string[] {
  if (ctx.columnFilters.agency.length > 0) return ctx.columnFilters.agency;
  return ctx.agencyNames;
}

/** Período efetivo para GET /changes — respeita o filtro escolhido (incl. TOTAL). */
function resolveChangesPublicationRange(ctx: DashboardSearchContext): {
  start: string | null;
  end: string | null;
} {
  return periodToApiRange(ctx.period, ctx.publicationDateMin);
}

/** Parâmetros para GET /changes — mesma semântica em tabela, modal e foco de linha. */
export function buildChangesQueryParams(
  ctx: DashboardSearchContext,
  extra?: {
    sort_by?: string;
    sort_order?: string;
    page?: number;
    page_size?: number;
  },
): Record<string, unknown> {
  const range = resolveChangesPublicationRange(ctx);
  const agencies = resolveAgencyNames(ctx);

  const params: Record<string, unknown> = {
    published_start: range.start || null,
    published_end: range.end || null,
    sort_by: extra?.sort_by ?? 'published_at',
    sort_order: extra?.sort_order ?? 'desc',
  };

  if (agencies.length > 0) {
    params.agencies = agencies.join(',');
  } else if (ctx.agencyKeys.length > 0) {
    params.sources = ctx.agencyKeys.join(',');
  }

  if (ctx.assunto) params.assunto = ctx.assunto;
  if (ctx.keyword) params.q = ctx.keyword;
  if (ctx.monitoredNormAssunto) params.monitored_norm_assunto = true;
  if (ctx.monitoredAgency) params.monitored_agency = true;
  if (ctx.greeningQaOnly) params.greening_qa_only = true;

  const { state, lawType, changeType } = ctx.columnFilters;
  if (state.length) params.states = state.join(',');
  if (lawType.length) params.legislation_types = lawType.join(',');
  if (changeType.length) params.change_types = changeType.join(',');

  if (extra?.page != null) params.page = extra.page;
  if (extra?.page_size != null) params.page_size = extra.page_size;

  return params;
}

/** Parâmetros para GET /dashboard/stats (gráficos e breakdown contextual). */
export function buildStatsQueryInput(
  ctx: Pick<DashboardSearchContext, 'period' | 'publicationDateMin' | 'agencyNames' | 'assunto' | 'columnFilters'>,
  options?: { chartLocalOnly?: boolean },
): {
  dateRange: { start: string | null; end: string | null };
  agencies?: string[];
  context?: DashboardStatsContextFilters;
} {
  const agencies = resolveAgencyNames(ctx as DashboardSearchContext);
  const dateRange = periodToApiRange(ctx.period, ctx.publicationDateMin);
  const chartLocalOnly = options?.chartLocalOnly ?? false;

  const context: DashboardStatsContextFilters = {};
  if (!chartLocalOnly) {
    if (ctx.assunto) context.assunto = ctx.assunto;
    const { state, lawType, changeType } = ctx.columnFilters;
    if (state.length) context.states = state.join(',');
    if (lawType.length) context.legislation_types = lawType.join(',');
    if (changeType.length) context.change_types = changeType.join(',');
  }

  return {
    dateRange,
    agencies: agencies.length > 0 ? agencies : undefined,
    context: Object.keys(context).length > 0 ? context : undefined,
  };
}

export type DashboardChartKey = 'source' | 'timeline' | 'type' | 'lawType';
